/**
 * Daily Trading Activity — shadcn area chart (blue total, green wins, red losses).
 */

import React, { useEffect, useMemo } from "react";
import { Area, CartesianGrid, ComposedChart, XAxis, YAxis } from "recharts";
import { Box, Skeleton, Text, useColorModeValue } from "@chakra-ui/react";
import { useTheme } from "next-themes";
import { Card, CardContent, CardDescription, CardHeader } from "@/components/ui/card";
import {
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from "@/components/ui/chart";
import { cn } from "@/lib/utils";
import { TradeStatisticsFilters } from "../../../../types/trade";
import { useDailyActivity } from "../../../../hooks/useTradeStatistics";
import { useStatisticsWidgetErrorGate } from "../../../../hooks/useStatisticsWidgetErrorGate";
import { StatisticsInlineError } from "../../../../components/errors/StatisticsInlineError";
import { useDisplayTimeZone } from "../../../../contexts/DisplayTimezoneContext";
import {
  formatChartHourAxisLabel,
  formatYmdBucketShortInZone,
  parseStatisticsChartInstant,
  shouldIncludeYearOnChartAxis,
} from "../../../../utils/displayDateTime";
import {
  hourlyChartSpansMultipleDays,
  resolveChartTimeGrain,
} from "../../../../utils/chartDataSampling";
import {
  StatisticsCardHeadline,
  STATS_FONT_FAMILY,
  useStatsCardHeadlineColor,
} from "./statisticsCardHeadline";
import {
  statisticsShadcnBarChartAreaClassName,
  statisticsShadcnCardClassName,
  statisticsShadcnCardContentClassName,
  statisticsShadcnDailyActivityChartContainerClassName,
} from "./statisticsShadcnCardShell";
import {
  STATS_CHART_TOOLTIP_TITLE_CLASS,
  STATISTICS_NO_DATA_TIMEFRAME_MESSAGE,
  StatisticsChartTooltipRow,
  statsChartTooltipContentClassName,
} from "./statisticsChartTooltip";

const BRAND_NEON_AQUA = "#3EEAEC";
const BRAND_GLACIAL_GREEN = "#06E363";
const BRAND_NEON_RED = "#F70009";

const chartConfig = {
  totalTrades: {
    label: "Total Trades",
    color: BRAND_NEON_AQUA,
  },
  winningTrades: {
    label: "Winning Trades",
    color: BRAND_GLACIAL_GREEN,
  },
  losingTrades: {
    label: "Losing Trades",
    color: BRAND_NEON_RED,
  },
} satisfies ChartConfig;

const LEGEND_SERIES_ORDER = ["winningTrades", "losingTrades", "totalTrades"] as const;

function legendSeriesIndex(dataKey: string | undefined): number {
  const index = LEGEND_SERIES_ORDER.indexOf(
    dataKey as (typeof LEGEND_SERIES_ORDER)[number]
  );
  return index === -1 ? LEGEND_SERIES_ORDER.length : index;
}

function DailyActivityLegendContent(
  props: React.ComponentProps<typeof ChartLegendContent>
) {
  const sortedPayload = props.payload
    ? [...props.payload].sort(
        (a, b) =>
          legendSeriesIndex(String(a.dataKey)) - legendSeriesIndex(String(b.dataKey))
      )
    : props.payload;

  return (
    <ChartLegendContent
      {...props}
      payload={sortedPayload}
      className={cn(
        "text-gray-500 dark:text-sidebar-muted-foreground font-[family-name:var(--font-primary)] text-sm font-medium",
        props.className
      )}
    />
  );
}

type ActivityChartDatum = {
  label: string;
  totalTrades: number;
  winningTrades: number;
  losingTrades: number;
};

interface DailyActivityChartProps {
  filters?: TradeStatisticsFilters;
}

const DailyActivityChart: React.FC<DailyActivityChartProps> = ({ filters }) => {
  const { resolvedTheme } = useTheme();
  const displayTimeZone = useDisplayTimeZone();
  const axisTickColor = useStatsCardHeadlineColor();
  const errorColor = useColorModeValue("red.500", "red.300");
  const themeClass = resolvedTheme === "dark" ? "dark" : "";

  const { data, loading, error, errorAction } = useDailyActivity(filters);
  const { showWidgetError, widgetError, widgetAction } = useStatisticsWidgetErrorGate(error, errorAction);

  const {
    chartData,
    hasWinLossData,
    mostActiveLabel,
    mostActiveCount,
    description,
  } = useMemo(() => {
    const pointDates = data?.daily_data_points?.map((point) => point.date) ?? [];
    const timeGrain = resolveChartTimeGrain({
      responseGrain: data?.period,
      requestGrain: filters?.period,
      dates: pointDates,
    });
    const isHourly = timeGrain === "hourly";
    const hourlyIncludeDay = isHourly && hourlyChartSpansMultipleDays(pointDates);

    if (!data?.daily_data_points?.length) {
      return {
        chartData: [] as ActivityChartDatum[],
        hasWinLossData: false,
        mostActiveLabel: "N/A",
        mostActiveCount: 0,
        description: isHourly
          ? "Trade count by hour for the selected period"
          : "Trade count by day for the selected period",
      };
    }

    const dailyDataPoints = data.daily_data_points;
    const recentData = isHourly ? dailyDataPoints : dailyDataPoints.slice(-30);

    const ymdBuckets = dailyDataPoints
      .map((point) =>
        point.date.length >= 10 ? point.date.slice(0, 10) : point.date.split(" ")[0] || point.date
      )
      .filter(Boolean);
    const includeYear = !isHourly && shouldIncludeYearOnChartAxis(ymdBuckets);

    const rows: ActivityChartDatum[] = recentData.map((point) => ({
      label: (() => {
        if (isHourly) {
          const parsed = parseStatisticsChartInstant(point.date);
          if (parsed) {
            return formatChartHourAxisLabel(parsed, displayTimeZone, {
              includeDay: hourlyIncludeDay,
            });
          }
          return point.date;
        }
        const ymd =
          point.date.length >= 10 ? point.date.slice(0, 10) : point.date.split(" ")[0] || point.date;
        return formatYmdBucketShortInZone(ymd, displayTimeZone, { includeYear });
      })(),
      totalTrades: point.trade_count,
      winningTrades: point.winning_trades ?? 0,
      losingTrades: point.losing_trades ?? 0,
    }));

    const winLoss =
      rows.some((row) => row.winningTrades > 0) || rows.some((row) => row.losingTrades > 0);

    let peak = rows[0];
    for (const row of rows) {
      if (row.totalTrades > peak.totalTrades) {
        peak = row;
      }
    }

    const dayMap: Record<string, string> = {
      Monday: "Mon",
      Tuesday: "Tue",
      Wednesday: "Wed",
      Thursday: "Thu",
      Friday: "Fri",
      Saturday: "Sat",
      Sunday: "Sun",
    };

    let mostActiveLabel = peak?.label ?? "N/A";
    let mostActiveCount = peak?.totalTrades ?? 0;

    if (!isHourly && data.day_of_week_summary?.length) {
      const topDay = data.day_of_week_summary.reduce((max, day) =>
        day.total_trades > max.total_trades ? day : max
      );
      mostActiveLabel = dayMap[topDay.day] || topDay.day.substring(0, 3);
      mostActiveCount = topDay.total_trades;
    } else if (isHourly) {
      mostActiveLabel = peak.label;
      mostActiveCount = peak.totalTrades;
    }

    return {
      chartData: rows,
      hasWinLossData: winLoss,
      mostActiveLabel,
      mostActiveCount,
      description: isHourly
        ? `Most active hour: ${mostActiveLabel} (${mostActiveCount} trades)`
        : `Most active day: ${mostActiveLabel} (${mostActiveCount} trades)`,
    };
  }, [data, filters?.period, displayTimeZone]);



  const cardShell = (children: React.ReactNode) => (
    <Card
      data-statistics-shadcn-card
      className={cn(statisticsShadcnCardClassName, themeClass)}
      style={{ fontFamily: STATS_FONT_FAMILY }}
    >
      <CardHeader className="border-b border-border px-[22px] pb-4 pt-[22px]">
        <StatisticsCardHeadline>Daily Trading Activity</StatisticsCardHeadline>
        <CardDescription className="text-xs" style={{ fontFamily: STATS_FONT_FAMILY }}>
          {description}
        </CardDescription>
      </CardHeader>
      <CardContent className={cn(statisticsShadcnCardContentClassName, "pt-4")}>
        <div className={statisticsShadcnBarChartAreaClassName}>{children}</div>
      </CardContent>
    </Card>
  );

  if (loading) {
    return cardShell(
      <Box h="300px" w="100%">
        <Skeleton height="100%" borderRadius="md" />
      </Box>
    );
  }

  if (showWidgetError && widgetError) {
    return cardShell(<StatisticsInlineError message={widgetError} action={widgetAction ?? undefined} />);
  }

  if (chartData.length === 0) {
    return cardShell(
      <Box
        h="300px"
        w="100%"
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <Text fontSize="sm" color="gray.500" fontFamily={STATS_FONT_FAMILY}>
          {STATISTICS_NO_DATA_TIMEFRAME_MESSAGE}
        </Text>
      </Box>
    );
  }

  return cardShell(
    <ChartContainer
      config={chartConfig}
      className={statisticsShadcnDailyActivityChartContainerClassName}
      style={{ fontFamily: STATS_FONT_FAMILY }}
    >
      <ComposedChart
        accessibilityLayer
        data={chartData}
        margin={{
          top: 16,
          right: 24,
          left: 16,
          bottom: hasWinLossData ? 12 : 20,
        }}
      >
        <defs />
        <CartesianGrid vertical={false} strokeDasharray="4 4" className="stroke-border/50" />
        <YAxis
          tickLine={false}
          axisLine={false}
          tickMargin={8}
          width={36}
          allowDecimals={false}
          tick={{ fill: axisTickColor, fontSize: 11 }}
        />
        <XAxis
          dataKey="label"
          tickLine={false}
          axisLine={false}
          tickMargin={8}
          minTickGap={32}
          interval="preserveStartEnd"
          tick={{ fill: axisTickColor, fontSize: 11 }}
        />
        <ChartTooltip
          cursor={false}
          content={
            <ChartTooltipContent
              className={statsChartTooltipContentClassName("w-[200px]")}
              labelClassName={STATS_CHART_TOOLTIP_TITLE_CLASS}
              indicator="dot"
              labelFormatter={(_value, payload) => {
                const row = payload?.[0]?.payload as ActivityChartDatum | undefined;
                return row?.label ?? "";
              }}
              formatter={(value, name) => {
                const n = Number(value);
                const label = chartConfig[name as keyof typeof chartConfig]?.label ?? name;
                return (
                  <StatisticsChartTooltipRow
                    label={String(label)}
                    value={`${n} trade${n !== 1 ? "s" : ""}`}
                  />
                );
              }}
            />
          }
        />
        {hasWinLossData ? (
          <>
            <Area
              dataKey="losingTrades"
              type="monotone"
              fill="transparent"
              stroke={chartConfig.losingTrades.color}
              strokeWidth={2}
              isAnimationActive={false}
            />
            <Area
              dataKey="winningTrades"
              type="monotone"
              fill="transparent"
              stroke={chartConfig.winningTrades.color}
              strokeWidth={2}
              isAnimationActive={false}
            />
            <Area
              dataKey="totalTrades"
              type="monotone"
              fill="transparent"
              stroke={chartConfig.totalTrades.color}
              strokeWidth={2}
              strokeDasharray="5 5"
              isAnimationActive={false}
            />
          </>
        ) : (
          <Area
            dataKey="totalTrades"
            type="monotone"
            fill="transparent"
            stroke={chartConfig.totalTrades.color}
            strokeWidth={2}
            strokeDasharray="5 5"
            isAnimationActive={false}
          />
        )}
        {hasWinLossData ? <ChartLegend content={<DailyActivityLegendContent />} /> : null}
      </ComposedChart>
    </ChartContainer>
  );
};

export default DailyActivityChart;
